import { defineStore } from 'pinia'
import { api } from '@/plugins/api'

/**
 * Provider notification registration: where a calibration provider is told that
 * inspections shared with them have changed, and with what credential.
 *
 * The token is WRITE-ONLY throughout. No read path returns it -- the API answers
 * only whether one is set -- so the form can never repopulate it and must treat a
 * blank field as "leave the stored one alone" rather than "clear it".
 */
/**
 * The registration reads answer with a JSON array, because the same proc also
 * serves "every registration". A single firm is the first entry, or nothing.
 */
function firstRow(result) {
  if (!result) return null
  const rows = Array.isArray(result) ? result : [result]
  return rows.length ? rows[0] : null
}

export const notificationStore = defineStore('notification', {
  state: () => ({
    loading: {
      registration: false,
      saving: false,
      testing: false
    },
    // null means "not loaded yet"; an object with no url means "none registered".
    registration: null,
    // The most recent test result, kept so the outcome stays on screen rather
    // than flashing past.
    testResult: null
  }),
  actions: {
    /*
      api.get/post return the PARSED BODY, not an axios-style { data } wrapper --
      see handleResponse in plugins/api. Reading .data off it yields undefined,
      which fails silently: the call succeeds, the state is set to nothing, and the
      screen simply never updates.
    */
    async getRegistration(launchpadFirm) {
      this.loading.registration = true
      this.testResult = null
      try {
        const result = await api.get(`notification/registration?launchpadFirm=${launchpadFirm}`)
        this.registration = firstRow(result)
        return this.registration
      } finally {
        this.loading.registration = false
      }
    },

    async saveRegistration(payload) {
      this.loading.saving = true
      try {
        const result = await api.post('notification/registration', payload)
        /*
          Save re-runs the same read the GET uses, so it comes back as an ARRAY
          too. Assigning it straight to state left registration.url undefined,
          which is why the save looked like it failed and the test button never
          enabled.
        */
        this.registration = firstRow(result)
        return this.registration
      } finally {
        this.loading.saving = false
      }
    },

    /**
     * Send a test delivery now. Bypasses the queue and the cycle, so the answer
     * comes back on this call rather than three minutes later.
     */
    async sendTest(launchpadFirm) {
      this.loading.testing = true
      this.testResult = null
      try {
        this.testResult = await api.post(`notification/test?launchpadFirm=${launchpadFirm}`)
        return this.testResult
      } catch (e) {
        // Surfaced in the panel rather than thrown away: a failed test is the
        // normal outcome while someone is getting a configuration right.
        this.testResult = { ok: false, error: e?.message || 'The test could not be sent.' }
        return this.testResult
      } finally {
        this.loading.testing = false
      }
    },

    clearTest() {
      this.testResult = null
    }
  }
})
